import React, { useState, useRef } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import { slice1Actions } from "../../store/reducer";
import { AUTHORIZATION_LEVEL } from "../../Common/Common";
import Place from "../../Models/Place";
import Section from "../../Models/Section";
import "./Modal.css";

const USER_ID_THRESHOLD = 999;

// ── CSV helpers ────────────────────────────────────────────────────────────────

function splitCSVLine(line) {
  const fields = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === "," && !inQ) {
      fields.push(cur); cur = "";
    } else {
      cur += c;
    }
  }
  fields.push(cur);
  return fields;
}

function parseCSVSection(lines) {
  if (lines.length < 2) return [];
  const headers = splitCSVLine(lines[0]);
  return lines.slice(1).map((line) => {
    const vals = splitCSVLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ""; });
    return obj;
  });
}

function splitCSVSections(text) {
  const sections = {};
  let currentKey = null;
  let currentLines = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    const m = line.match(/^===\s*(.+?)\s*===$/);
    if (m) {
      if (currentKey) sections[currentKey] = currentLines;
      currentKey = m[1];
      currentLines = [];
    } else if (currentKey !== null && line !== "") {
      currentLines.push(raw);
    }
  }
  if (currentKey) sections[currentKey] = currentLines;
  return sections;
}

// ── misc helpers ───────────────────────────────────────────────────────────────

const parseBool = (v) => v === true || v === "true" || v === 1;

const parseArr = (v) =>
  Array.isArray(v) ? v
  : typeof v === "string" && v ? v.split(";").filter(Boolean)
  : [];

// event.Places is comma-joined (unlike the semicolon-joined Group/Category
// arrays), so it needs its own remap helper.
const remapCommaIds = (v, map) =>
  (v || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => map[String(id)] ?? String(id))
    .join(",");

const toISO = (v) => {
  if (!v) return "";
  if (typeof v?.toISO === "function") return v.toISO();
  const dt = DateTime.fromISO(String(v), { zone: "utc" });
  return dt.isValid ? dt.toISO() : String(v);
};

function scheduleNamesFromEvents(events) {
  const seen = new Set(["Main"]);
  for (const e of events || []) {
    const it = (e.Itinerary || "Main").replace(/%20/g, " ");
    seen.add(it);
  }
  return [...seen].sort();
}

// ── parsers ────────────────────────────────────────────────────────────────────

function parseJSON(text) {
  return JSON.parse(text);
}

function parseCSV(text) {
  const sections = splitCSVSections(text);
  const toObjs = (key) => parseCSVSection(sections[key] || []);

  let guide_info = null;
  const infoRows = toObjs("GUIDE INFO");
  if (infoRows.length) {
    guide_info = {};
    for (const row of infoRows) {
      const { key, value = "" } = row;
      if (!key) continue;
      if (key === "countries" || key === "cities") {
        guide_info[key] = value ? value.split(";").filter(Boolean) : [];
      } else if (key === "base_location" || key === "destinations" || key === "aiPreferences" || key === "flights" || key === "guide_settings" || key === "tripMembers" || key === "expenses") {
        if (value && !value.startsWith("[object")) {
          try { guide_info[key] = JSON.parse(value); } catch { /* skip */ }
        }
      } else {
        guide_info[key] = value;
      }
    }
    guide_info.custom_icons = toObjs("CUSTOM ICONS").map((r) => ({
      id: r.id, name: r.name, poi_img: r.poi_img, url: r.url,
    }));
  }

  const sectionRows = toObjs("CHAPTERS & SECTIONS");
  const chapMap = {};
  for (const row of sectionRows) {
    if (!row.chapter_title) continue;
    if (!chapMap[row.chapter_title]) {
      chapMap[row.chapter_title] = {
        Title: row.chapter_title, ID: row.chapter_ID, sections: [],
      };
    }
    chapMap[row.chapter_title].sections.push({
      Title: row.section_title, ID: row.section_ID, html: "",
      PlaceIds:   parseArr(row.PlaceIds),
      Groups:     parseArr(row.Groups),
      Categories: parseArr(row.Categories),
    });
  }

  return {
    guide_info,
    places:     toObjs("PLACES"),
    events:     toObjs("EVENTS"),
    groups:     toObjs("GROUPS (user-defined)"),
    categories: toObjs("CATEGORIES (user-defined)"),
    chapters:   Object.values(chapMap),
  };
}

// ── import engine ──────────────────────────────────────────────────────────────

function resolveCategory(guide, name) {
  if (!name) return null;
  const idx = guide.categories.findIndex(
    (c) => c.name.toUpperCase() === name.toUpperCase()
  );
  if (idx !== -1) return guide.categories[idx].id;
  guide.addCategory(name);
  return guide.categories[guide.categories.length - 1].id;
}

function buildGroupIdMap(guide, exportedGroups) {
  const map = {};
  for (const g of exportedGroups) {
    const existed = guide.findGroupFromName(g.name);
    const localId = existed ? existed.id : guide.addGroup(g.name);
    if (localId == null) continue;
    map[String(g.id)] = String(localId);
    // Restore poi_img/schedule/mosaic/image for newly created groups only —
    // matched-by-name groups keep whatever local settings they already have.
    if (!existed) {
      const newGroup = guide.groups.find((x) => x.id === String(localId));
      if (newGroup) {
        if (g.poi_img && g.poi_img !== "0") newGroup.poi_img = g.poi_img;
        if (g.schedule) newGroup.schedule = g.schedule;
        if (g.mosaic === true || g.mosaic === "true") newGroup.mosaic = true;
        if (g.image) newGroup.image = g.image;
      }
    }
  }
  return map;
}

function buildCategoryIdMap(guide, exportedCategories) {
  const map = {};
  for (const c of exportedCategories) {
    const localId = resolveCategory(guide, c.name);
    if (localId != null) map[String(c.id)] = String(localId);
  }
  return map;
}

function remapIds(ids, map) {
  return parseArr(ids).map((id) => map[String(id)] ?? String(id));
}

function localFindChapter(guide, title) {
  return (guide.chapters || []).find(
    (ch) => (ch.Title || "").toUpperCase() === (title || "").toUpperCase()
  );
}

function localFindSection(chapter, title) {
  return (chapter?.sections || []).find(
    (s) => (s.Title || "").toUpperCase() === (title || "").toUpperCase()
  );
}

function importData(data, { overwriteSections = true } = {}) {
  const guide = window.guide_obj;
  const {
    guide_info   = null,
    places       = [],
    events       = [],
    groups       = [],
    categories   = [],
    chapters     = [],
  } = data;

  let importedGuideInfo   = false;
  let importedCustomIcons = 0;
  let addedPlaces         = 0;
  let skippedPlaces       = 0;
  let addedEvents         = 0;
  let skippedEvents       = 0;
  let addedGroups         = 0;
  let reusedGroups        = 0;
  let addedCategories     = 0;
  let reusedCategories    = 0;
  let addedChapters       = 0;
  let addedSections       = 0;
  let updatedSections     = 0;
  let addedTripMembers    = 0;
  let addedExpenses       = 0;

  // ── 0. guide info ────────────────────────────────────────────────────────
  if (guide_info) {
    const attrPatch = {};
    if (guide_info.guide_name)  attrPatch.guide_name  = guide_info.guide_name;
    if (guide_info.description) attrPatch.description = guide_info.description;
    if (guide_info.image)       attrPatch.image       = guide_info.image;
    if (guide_info.image_m)     attrPatch.image_m     = guide_info.image_m;
    if (guide_info.language)    attrPatch.language    = guide_info.language;
    if (guide_info.base_location &&
        typeof guide_info.base_location === "object" &&
        guide_info.base_location.latitude != null) {
      attrPatch.base_location = guide_info.base_location;
    }
    const importedCountries = parseArr(guide_info.countries);
    if (importedCountries.length) attrPatch.countries = importedCountries;
    const importedCities = parseArr(guide_info.cities);
    if (importedCities.length) attrPatch.cities = importedCities;

    if (Object.keys(attrPatch).length) {
      Object.assign(guide.attributes, attrPatch);
      importedGuideInfo = true;
    }

    if (guide_info.document) {
      guide.document = guide_info.document;
      importedGuideInfo = true;
    }

    if (Array.isArray(guide_info.destinations) && guide_info.destinations.length) {
      // Same {zone:"utc"} decode Guide.js's decodeGuideFb uses for the
      // Firestore round-trip — dateStart/dateEnd come back as plain ISO
      // strings through JSON, not Luxon DateTimes.
      guide.destinations = guide_info.destinations.map((d) => ({
        ...d,
        dateStart: DateTime.fromISO(d.dateStart, { zone: "utc" }),
        dateEnd: DateTime.fromISO(d.dateEnd, { zone: "utc" }),
      }));
      importedGuideInfo = true;
    }
    if (guide_info.aiPreferences) {
      guide.aiPreferences = guide_info.aiPreferences;
      importedGuideInfo = true;
    }
    if (Array.isArray(guide_info.flights) && guide_info.flights.length) {
      guide.flights = guide_info.flights;
      importedGuideInfo = true;
    }
    if (guide_info.guide_settings) {
      guide.guide_settings = guide_info.guide_settings;
      importedGuideInfo = true;
    }

    for (const ic of guide_info.custom_icons || []) {
      if (!ic.name) continue;
      if (guide.custom_icons.some(
        (x) => x.name.toUpperCase() === ic.name.toUpperCase()
      )) continue;
      guide.addCustomIcon(ic.name);
      const newIcon = guide.custom_icons[guide.custom_icons.length - 1];
      if (newIcon && (ic.poi_img || ic.url)) {
        guide.modifyCustomIcon(newIcon, {
          name: ic.name,
          poi_img: ic.poi_img || "0",
          url: ic.url || "",
        });
      }
      importedCustomIcons++;
    }
  }

  // ── 1. groups & categories (id remap) ────────────────────────────────────
  const groupsToImport = groups.filter((g) => parseInt(g.id, 10) > USER_ID_THRESHOLD);
  const catsToImport   = categories.filter((c) => parseInt(c.id, 10) > USER_ID_THRESHOLD);

  const groupsCountBefore     = guide.groups.length;
  const categoriesCountBefore = guide.categories.length;

  const groupIdMap    = buildGroupIdMap(guide, groupsToImport);
  const categoryIdMap = buildCategoryIdMap(guide, catsToImport);

  addedGroups      = guide.groups.length      - groupsCountBefore;
  reusedGroups     = groupsToImport.length    - addedGroups;
  addedCategories  = guide.categories.length  - categoriesCountBefore;
  reusedCategories = catsToImport.length      - addedCategories;

  // ── 2. places ─────────────────────────────────────────────────────────────
  // Maps each place's exported (source-guide) ID to its local ID, so event
  // Places references (§3 below) can be remapped the same way Group/Category
  // IDs are — otherwise imported events keep pointing at place IDs that only
  // ever existed in the source guide.
  const placeIdMap = {};
  for (const p of places) {
    const existingPlace = guide.places.placesList.find(
      (ex) =>
        ex.Name === p.Name &&
        Math.abs(parseFloat(ex.Lat)   - parseFloat(p.Lat))   < 0.0001 &&
        Math.abs(parseFloat(ex.Longi) - parseFloat(p.Longi)) < 0.0001
    );
    if (existingPlace) {
      // Duplicate place — don't overwrite it, but still merge in any group
      // membership from the import (e.g. a mosaic group) it doesn't already have.
      if (p.ID != null) placeIdMap[String(p.ID)] = String(existingPlace.ID);
      const importedGroupIds = remapIds(p.Group, groupIdMap);
      const mergedGroupIds = [...new Set([...(existingPlace.Group || []), ...importedGroupIds])];
      if (mergedGroupIds.length !== (existingPlace.Group || []).length) {
        const updated = JSON.parse(JSON.stringify(existingPlace));
        updated.Group = mergedGroupIds;
        guide.places.modifyPlace(updated, existingPlace.ID);
      }
      skippedPlaces++;
      continue;
    }

    const np = new Place(guide.places);
    np.Name     = p.Name     || "";
    np.Lat      = parseFloat(p.Lat)   || 0;
    np.Longi    = parseFloat(p.Longi) || 0;
    np.Address  = p.Address  || "";
    np.City     = p.City     || "";
    np.State    = p.State    || "";
    np.Country  = p.Country  || "";
    np.Phone    = p.Phone    || "";
    np.Email    = p.Email    || "";
    np.Rating   = p.Rating  ?? "";
    np.Hlink    = p.Hlink    || "";
    np.Info     = p.Info     || "";
    np.Photo    = p.Photo    || "";
    np.Photo_m  = p.Photo_m  || "";
    np.PType    = p.PType    || "Icon";
    np.Favorite   = parseBool(p.Favorite);
    np.Visited    = parseBool(p.Visited);
    np.MustSee    = parseBool(p.MustSee);
    np.BucketList = parseBool(p.BucketList);
    np.Hidden     = parseBool(p.Hidden);
    np.Duration = parseInt(p.Duration, 10) || 30;
    np.Group    = remapIds(p.Group,    groupIdMap);
    np.Category = remapIds(p.Category, categoryIdMap);

    const newId = guide.places.addPlace(np);
    if (p.ID != null) placeIdMap[String(p.ID)] = String(newId);
    addedPlaces++;
  }

  // ── 2.5 trip members & expenses (Expense Tracking MVP) ───────────────────
  // Runs after placeIdMap/categoryIdMap exist (steps 1-2 above) so
  // expenses.placeId/categoryId can be remapped the same way Place.Group/
  // Category already are. Members are merged by name (case-insensitive),
  // same convention buildGroupIdMap/buildCategoryIdMap use. eventKey is
  // dropped rather than remapped — unlike Place.ID, an event's
  // CommentKey/stableEventKey has no stable cross-guide identity for
  // addEvent (§3 below) to preserve, so there's nothing reliable to map it
  // to.
  if (!Array.isArray(guide.tripMembers)) guide.tripMembers = [];
  if (!Array.isArray(guide.expenses)) guide.expenses = [];
  const tripMemberIdMap = {};
  for (const m of guide_info?.tripMembers || []) {
    if (!m?.name) continue;
    const existingMember = guide.tripMembers.find(
      (x) => x.name.toUpperCase() === m.name.toUpperCase()
    );
    if (existingMember) {
      tripMemberIdMap[String(m.id)] = existingMember.id;
      continue;
    }
    const newMemberId = `tm_${Date.now()}_${Math.floor(Math.random() * 100000)}_${addedTripMembers}`;
    guide.tripMembers.push({ id: newMemberId, name: m.name });
    tripMemberIdMap[String(m.id)] = newMemberId;
    addedTripMembers++;
  }
  for (const ex of guide_info?.expenses || []) {
    if (ex == null || isNaN(parseFloat(ex.amount))) continue;
    guide.expenses.push({
      id: `exp_${Date.now()}_${Math.floor(Math.random() * 100000)}_${addedExpenses}`,
      amount: parseFloat(ex.amount),
      currency: ex.currency || "USD",
      categoryId: ex.categoryId != null ? (categoryIdMap[String(ex.categoryId)] ?? ex.categoryId) : "",
      placeId: ex.placeId != null ? (placeIdMap[String(ex.placeId)] ?? null) : null,
      eventKey: null,
      date: ex.date || "",
      paidBy: ex.paidBy != null ? (tripMemberIdMap[String(ex.paidBy)] ?? null) : null,
      splits: (ex.splits || []).map((s) => ({
        memberId: tripMemberIdMap[String(s.memberId)] ?? s.memberId,
        percent: s.percent,
      })),
      title: ex.title || "", // optional heading, added 2026-10-09
      note: ex.note || "",
    });
    addedExpenses++;
  }
  if (addedTripMembers > 0 || addedExpenses > 0) importedGuideInfo = true;

  // ── 3. events ─────────────────────────────────────────────────────────────
  for (const e of events) {
    const startISO  = toISO(e.Start);
    const itinerary = (e.Itinerary || "Main").replace(/%20/g, " ");
    const isDuplicate = guide.schedule.events.some(
      (ex) =>
        ex.Subject === e.Subject &&
        toISO(ex.Start) === startISO &&
        (ex.Itinerary || "Main").replace(/%20/g, " ") === itinerary
    );
    if (isDuplicate) { skippedEvents++; continue; }

    guide.schedule.addEvent({
      Subject:   e.Subject   || "",
      Start:     DateTime.fromISO(e.Start || "", { zone: "utc" }),
      End:       DateTime.fromISO(e.End   || "", { zone: "utc" }),
      Places:    remapCommaIds(e.Places, placeIdMap),
      Itinerary: e.Itinerary || "Main",
      Info:      e.Info      || "",
    });
    addedEvents++;
  }

  // ── 4. chapters & sections ────────────────────────────────────────────────
  for (const ch of chapters) {
    if (!ch.Title) continue;
    let localChapter = localFindChapter(guide, ch.Title);
    if (!localChapter) {
      const result = guide.addChapter(ch.Title);
      // addChapter may return the chapter object or nothing — find it either way
      localChapter = (result && result.Title) ? result : localFindChapter(guide, ch.Title);
      if (localChapter) addedChapters++;
    }
    if (!localChapter) continue;
    for (const s of ch.sections || []) {
      if (!s.Title) continue;
      const existing = localFindSection(localChapter, s.Title);
      if (existing) {
        if (overwriteSections && s.html) existing.html = s.html;
        updatedSections++;
      } else {
        const ns = new Section(guide.chapters, s.Title);
        ns.html       = s.html || "";
        ns.PlaceIds   = remapIds(s.PlaceIds, placeIdMap);
        ns.Groups     = remapIds(s.Groups,     groupIdMap);
        ns.Categories = remapIds(s.Categories, categoryIdMap);
        guide.addSection(localChapter.ID, ns);
        addedSections++;
      }
    }
  }

  return {
    importedGuideInfo, importedCustomIcons,
    addedPlaces,    skippedPlaces,
    addedEvents,    skippedEvents,
    addedGroups,    reusedGroups,
    addedCategories, reusedCategories,
    addedChapters,  addedSections, updatedSections,
    addedTripMembers, addedExpenses,
  };
}

// ── sub-components ─────────────────────────────────────────────────────────────

function ToggleRow({ label, count, checked, onChange }) {
  return (
    <div className="export-toggle-row">
      <span className="export-toggle-label">
        {label}
        {count != null && <span className="export-count-pill">{count}</span>}
      </span>
      <div className="form-check form-switch mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ cursor: "pointer" }}
        />
      </div>
    </div>
  );
}

function SummaryRow({ label, value, muted }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: "0.88rem" }}>
      <span style={{ color: muted ? "#999" : undefined }}>{label}</span>
      <span style={{ fontWeight: 600, color: "#3598db", marginLeft: "1rem" }}>{value}</span>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export default function ImportTripModal({ show, onHide }) {
  const dispatch     = useDispatch();
  const Authorization = useSelector((state) => state.Authorization);
  const fileInputRef = useRef(null);

  const [phase, setPhase]                         = useState("pick");
  const [parsedData, setParsedData]               = useState(null);
  const [importPlaces, setImportPlaces]           = useState(true);
  const [selectedPlaceIds, setSelectedPlaceIds]   = useState(null); // null = all
  const [placesExpanded, setPlacesExpanded]       = useState(false);
  const [scheduleSelection, setScheduleSelection] = useState({});
  const [overwriteSections, setOverwriteSections] = useState(true);
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState(null);
  const [summary, setSummary]                     = useState(null);

  if (!show) return null;

  const handleClose = () => {
    // reset state when closing
    setPhase("pick");
    setParsedData(null);
    setError(null);
    setSummary(null);
    onHide();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // reset file input so the same file can be re-selected
    e.target.value = "";

    if (Authorization === AUTHORIZATION_LEVEL.GUEST) {
      setError("You need at least copy permission to import a trip.");
      return;
    }

    const ext = file.name.split(".").pop().toLowerCase();
    setLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        let data;
        if (ext === "json") {
          data = parseJSON(text);
        } else if (ext === "csv") {
          data = parseCSV(text);
        } else {
          try { data = parseJSON(text); }
          catch { data = parseCSV(text); }
        }

        const schedNames = scheduleNamesFromEvents(data.events);
        const sel = {};
        schedNames.forEach((n) => { sel[n] = true; });

        unstable_batchedUpdates(() => {
          setParsedData(data);
          setImportPlaces(true);
          setSelectedPlaceIds(null);
          setPlacesExpanded(false);
          setScheduleSelection(sel);
          setPhase("select");
          setLoading(false);
        });
      } catch (err) {
        setError("Could not read file: " + err.message);
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setError("Failed to read file.");
      setLoading(false);
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (!parsedData) return;

    const places = importPlaces
      ? (parsedData.places || []).filter((p, i) =>
          selectedPlaceIds === null || selectedPlaceIds.includes(String(p.ID ?? i))
        )
      : [];

    const selectedSchedules = Object.entries(scheduleSelection)
      .filter(([, v]) => v)
      .map(([k]) => k);
    const events = (parsedData.events || []).filter((e) => {
      const it = (e.Itinerary || "Main").replace(/%20/g, " ");
      return selectedSchedules.includes(it);
    });

    setLoading(true);
    setError(null);
    try {
      const result = importData({ ...parsedData, places, events }, { overwriteSections });
      dispatch(slice1Actions.setGuide({ guide: null, update_guide_attributes: true }));
      setSummary(result);
      setPhase("done");
    } catch (err) {
      setError("Import failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSchedule = (name, value) =>
    setScheduleSelection((prev) => ({ ...prev, [name]: value }));

  // ── render phases ──────────────────────────────────────────────────────────

  const CSV_EXAMPLE =
`EVENTS
Subject,Start,End,Itinerary,Places,Info
Arrive at airport,2026-06-10 08:00,2026-06-10 10:00,Main,,
Check in to hotel,2026-06-10 14:00,2026-06-10 15:00,Main,,
Dinner at restaurant,2026-06-10 19:00,2026-06-10 21:00,Main,,`;

  const renderPick = () => (
    <>
      <p style={{ color: "#888", fontSize: "0.88rem", marginBottom: "0.75rem" }}>
        Accepts JSON or CSV files exported from Andiamo Places.
        Duplicates are skipped. Missing fields are ignored.
      </p>
      <details style={{ marginBottom: "1rem" }}>
        <summary style={{ color: "#888", fontSize: "0.85rem", cursor: "pointer", userSelect: "none" }}>
          CSV events example
        </summary>
        <pre style={{
          marginTop: "0.5rem",
          padding: "0.6rem 0.75rem",
          background: "var(--bs-tertiary-bg, #f8f9fa)",
          borderRadius: "6px",
          fontSize: "0.78rem",
          overflowX: "auto",
          whiteSpace: "pre",
          color: "var(--bs-body-color)",
          border: "1px solid var(--bs-border-color, #dee2e6)",
        }}>{CSV_EXAMPLE}</pre>
        <p style={{ color: "#888", fontSize: "0.8rem", marginTop: "0.4rem", marginBottom: 0 }}>
          Dates: <code>YYYY-MM-DD HH:MM</code> (24-hour). Itinerary defaults to <code>Main</code>.
        </p>
      </details>
      {error && <div className="alert alert-danger py-2" style={{ fontSize: "0.85rem" }}>{error}</div>}
      <input
        type="file"
        accept=".json,.csv"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <button
        className="btn btn-primary w-100"
        disabled={loading}
        onClick={() => fileInputRef.current?.click()}
      >
        {loading ? "Reading file…" : "Select File…"}
      </button>
    </>
  );

  const renderSelect = () => {
    const places      = parsedData?.places  || [];
    const allEvents   = parsedData?.events  || [];
    const schedNames  = scheduleNamesFromEvents(allEvents);

    const eventsForSchedule = (name) =>
      allEvents.filter((e) => (e.Itinerary || "Main").replace(/%20/g, " ") === name).length;

    const allIds = places.map((p, i) => String(p.ID ?? i));
    const effectiveSelected = selectedPlaceIds === null ? allIds : selectedPlaceIds;
    const togglePlace = (id) => {
      const next = effectiveSelected.includes(id)
        ? effectiveSelected.filter((x) => x !== id)
        : [...effectiveSelected, id];
      setSelectedPlaceIds(next.length === allIds.length ? null : next);
    };

    return (
      <>
        {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: "0.85rem" }}>{error}</div>}

        {/* Places section */}
        <div className="export-section-header">Places</div>
        <ToggleRow
          label="Import places"
          count={places.length}
          checked={importPlaces}
          onChange={(v) => { setImportPlaces(v); if (!v) setPlacesExpanded(false); }}
        />
        {importPlaces && places.length > 0 && (
          <>
            <div
              onClick={() => setPlacesExpanded((x) => !x)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                cursor: "pointer", padding: "4px 0 4px 8px", color: "#3598db", fontSize: "0.82rem" }}
            >
              <span>
                {selectedPlaceIds === null
                  ? `All ${places.length} places`
                  : `${selectedPlaceIds.length} of ${places.length} selected`}
              </span>
              <span style={{ fontSize: "0.7rem", marginRight: 2 }}>{placesExpanded ? "▲" : "▼"}</span>
            </div>
            {placesExpanded && (
              <div style={{ border: "1px solid #eee", borderRadius: 6, marginBottom: "0.5rem", maxHeight: 220, overflowY: "auto" }}>
                <div style={{ display: "flex", gap: "0.5rem", padding: "4px 8px", borderBottom: "1px solid #eee" }}>
                  <button className="btn btn-link btn-sm p-0" style={{ fontSize: "0.75rem" }}
                    onClick={() => setSelectedPlaceIds(null)}>All</button>
                  <button className="btn btn-link btn-sm p-0" style={{ fontSize: "0.75rem" }}
                    onClick={() => setSelectedPlaceIds([])}>None</button>
                </div>
                {places.map((p, i) => {
                  const id = String(p.ID ?? i);
                  const checked = effectiveSelected.includes(id);
                  return (
                    <label key={id} style={{ display: "flex", alignItems: "center", gap: "0.5rem",
                      padding: "4px 8px", cursor: "pointer", fontSize: "0.85rem",
                      borderBottom: "1px solid #f5f5f5" }}>
                      <input type="checkbox" checked={checked} onChange={() => togglePlace(id)} />
                      {p.Name || "(unnamed)"}
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Schedules section */}
        {schedNames.length > 0 && (
          <>
            <div className="export-section-header" style={{ marginTop: "1rem" }}>Schedules</div>
            {schedNames.map((name) => (
              <ToggleRow
                key={name}
                label={name}
                count={eventsForSchedule(name)}
                checked={scheduleSelection[name] ?? true}
                onChange={(v) => toggleSchedule(name, v)}
              />
            ))}
          </>
        )}

        {/* Chapters section */}
        {(parsedData?.chapters?.length > 0) && (
          <>
            <div className="export-section-header" style={{ marginTop: "1rem" }}>Chapters & Sections</div>
            <ToggleRow
              label="Overwrite existing section content"
              checked={overwriteSections}
              onChange={setOverwriteSections}
            />
          </>
        )}

        <p style={{ fontSize: "0.75rem", color: "#888", marginTop: "1rem", marginBottom: "1rem" }}>
          Guide info, groups, categories, chapters, trip members, and expenses are always imported from the file.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <button
            className="btn btn-primary w-100"
            disabled={loading}
            onClick={handleImport}
          >
            {loading ? "Importing…" : "Import"}
          </button>
          <button
            className="btn btn-outline-secondary w-100"
            disabled={loading}
            onClick={() => { setParsedData(null); setSelectedPlaceIds(null); setPlacesExpanded(false); setError(null); setPhase("pick"); }}
          >
            Choose a different file
          </button>
        </div>
      </>
    );
  };

  const renderDone = () => (
    <>
      <div className="export-section-header">Import complete</div>

      {(summary.importedGuideInfo || summary.importedCustomIcons > 0) && (
        <>
          <SummaryRow label="Guide info" value={summary.importedGuideInfo ? "updated" : "no changes"} muted={!summary.importedGuideInfo} />
          {summary.importedCustomIcons > 0 && <SummaryRow label="Custom icons added" value={summary.importedCustomIcons} />}
          <hr className="export-divider" />
        </>
      )}

      <SummaryRow label="Places added" value={summary.addedPlaces} />
      {summary.skippedPlaces > 0 && <SummaryRow label="Places skipped (duplicates)" value={summary.skippedPlaces} muted />}
      <hr className="export-divider" />

      <SummaryRow label="Events added" value={summary.addedEvents} />
      {summary.skippedEvents > 0 && <SummaryRow label="Events skipped (duplicates)" value={summary.skippedEvents} muted />}
      <hr className="export-divider" />

      <SummaryRow label="Groups created" value={summary.addedGroups} />
      {summary.reusedGroups > 0 && <SummaryRow label="Groups matched (already existed)" value={summary.reusedGroups} muted />}
      <SummaryRow label="Categories created" value={summary.addedCategories} />
      {summary.reusedCategories > 0 && <SummaryRow label="Categories matched" value={summary.reusedCategories} muted />}
      <hr className="export-divider" />

      <SummaryRow label="Chapters added" value={summary.addedChapters} />
      <SummaryRow label="Sections added" value={summary.addedSections} />
      {summary.updatedSections > 0 && <SummaryRow label="Sections updated" value={summary.updatedSections} />}
      {(summary.addedTripMembers > 0 || summary.addedExpenses > 0) && (
        <>
          <hr className="export-divider" />
          <SummaryRow label="Trip members added" value={summary.addedTripMembers} />
          <SummaryRow label="Expenses added" value={summary.addedExpenses} />
        </>
      )}

      <div style={{ marginTop: "1rem" }}>
        <button
          className="btn btn-outline-primary w-100"
          onClick={() => { setParsedData(null); setSummary(null); setError(null); setPhase("pick"); }}
        >
          Import another file
        </button>
      </div>
    </>
  );

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Import Trip</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "1rem" }}>
        {phase === "pick"   && renderPick()}
        {phase === "select" && renderSelect()}
        {phase === "done"   && renderDone()}
      </Modal.Body>
      {phase !== "pick" && (
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={handleClose}>Close</button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
